import { useFetcher } from '@remix-run/react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import TextInput from '~/components/Form/TextInput';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';
import { isInAppBrowser } from '~/utils/browser';

import LoginButtons from '../../LoginButtons';
import Tracked from '../../Tracked';
import type { ScreenProps } from '.';
import ModalHeadings from './ModalHeadings';
import OrSeparator from './OrSeperator';

function AuthenticationErrorMessage({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  if (!isActive) return null;

  return (
    <div className="flex flex-row items-center justify-start gap-2">
      <div className="shrink-0">
        <ErrorIcon />
      </div>
      <p className="text-sm font-semibold text-white">
        {t('login.stageSignIn.incorrectDetails')}
      </p>
    </div>
  );
}

export default function SignInScreen({
  onDismiss,
  setScreen,
  postAuthOptions,
  onError
}: ScreenProps) {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const { t } = useTranslation();

  const errors = fetcher.data?.errors;
  const idle = fetcher.state === 'idle';

  const onSignIn = useCallback(
    (loginType: LoginSource) => () => {
      gtm.sendLoginEvent(user.id, loginType);

      braze.setUser(user.id);
      braze.logLoginEvent();

      onDismiss({ loginSuccessful: true });
    },
    [user.id, onDismiss]
  );

  useEffect(() => {
    if (fetcher.data?.success === true) onSignIn(LoginSource.Email)();
  }, [fetcher.data?.success, onSignIn]);

  const isInApp = isInAppBrowser(navigator.userAgent);

  return (
    <>
      <ModalHeadings>{t('login.stageSignIn.subHeading')}</ModalHeadings>
      <fetcher.Form
        action={`/bolt/login/email${postAuthOptions?.sizes ? '?sizes=' + JSON.stringify(postAuthOptions.sizes) : ''}`}
        className="flex w-full flex-col items-start gap-4 bg-black"
        method="post"
      >
        <input
          type="hidden"
          value={postAuthOptions?.legacyProductId}
          name="legacyProductId"
        />
        <input
          type="hidden"
          value={postAuthOptions?.redirectUrl}
          name="redirectUrl"
        />
        <input type="hidden" value={user.guestId} name="guestId" />
        <input type="hidden" value={postAuthOptions?.action} name="action" />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={errors?.email ? t('login.emailFormatError') : undefined}
          id="login-email"
          name="email"
          placeholder={t('login.emailAddress')}
          type="email"
        />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={errors?.password ? t('login.passwordLengthError') : undefined}
          id="login-email"
          name="password"
          placeholder={t('login.password')}
          type="password"
        />
        <button
          onClick={() => setScreen('forgotPassword')}
          type="button"
          className="text-green-human"
        >
          {t('login.forgotPassword')}
        </button>
        <Tracked.Button
          className="h-12"
          variant="green-human"
          disabled={!idle}
          name="SIGN_IN_EMAIL"
          pagePosition={'Sign in modal'}
        >
          {idle ? t('login.signInButton') : t('login.signingIn')}
        </Tracked.Button>
        <AuthenticationErrorMessage
          isActive={Boolean(fetcher?.data?.errors?.authentication)}
        />
      </fetcher.Form>

      {!isInApp && (
        <>
          <OrSeparator />
          <div className="flex gap-4">
            <LoginButtons.Google
              pagePosition="Sign in modal"
              postAuthOptions={postAuthOptions}
              onSuccess={onSignIn(LoginSource.Google)}
              onError={onError}
            />
            <LoginButtons.Facebook
              pagePosition="Sign in modal"
              postAuthOptions={postAuthOptions}
              onSuccess={onSignIn(LoginSource.Meta)}
              onError={onError}
            />
            <LoginButtons.Apple
              pagePosition="Sign in modal"
              postAuthOptions={postAuthOptions}
              onSuccess={onSignIn(LoginSource.Apple)}
              onError={onError}
            />
          </div>
        </>
      )}
    </>
  );
}
